import React from "react"
import { createPortal } from "react-dom"
import {
  Building2, Package, X, Layers, MapPin, ExternalLink, Loader2, AlertCircle, FileText, Calendar, Clock, CreditCard, Wallet, ChevronRight
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useProductVariantDetails } from "@/hooks/seller/useProductVariantDetails"
import { useProductDetails } from "@/hooks/seller/useProductDetails"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// Company Detail Sidebar Component (Sliding Drawer)
export interface CompanyDetailSidebarProps {
  company: any | null
  onClose: () => void
  level?: number
}

export function CompanyDetailSidebar({ company, onClose, level = 0 }: CompanyDetailSidebarProps) {
  if (!company || typeof document === "undefined") return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200" 
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose} 
      />
      
      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <Building2 className="size-4 text-primary" /> Supplier Profile
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Sidebar Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-left text-xs font-semibold text-slate-700 scrollbar-thin">
          <div className="space-y-5">
            {/* Basic Info */}
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Company Name</span>
              <h4 className="text-sm font-extrabold text-slate-900 leading-tight">{company.name}</h4>
              {company.description && (
                <p className="text-slate-600 leading-relaxed font-normal whitespace-pre-line mt-1">
                  {company.description}
                </p>
              )}
              {company.tags && company.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {company.tags.map((tag: string) => (
                    <span key={tag} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-bold border border-slate-200/60 uppercase">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Profile Grid */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Company Overview</h5>
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                {company.industry && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Industry</span>
                    <span className="text-slate-800 font-bold block">{company.industry}</span>
                  </div>
                )}
                {company.companyType && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Business Type</span>
                    <span className="text-slate-800 font-bold block">{company.companyType}</span>
                  </div>
                )}
                {company.yearEstablished && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Established</span>
                    <span className="text-slate-800 font-bold block">{company.yearEstablished}</span>
                  </div>
                )}
                {company.employeeCount && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Employees</span>
                    <span className="text-slate-800 font-bold block">{company.employeeCount}</span>
                  </div>
                )}
                {company.annualRevenue && (
                  <div className="space-y-0.5 col-span-2 mt-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Annual Turnover</span>
                    <span className="text-slate-800 font-bold block">{company.annualRevenue}</span>
                  </div>
                )}
                {company.verificationStatus && (
                  <div className="space-y-0.5 col-span-2 mt-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Verification</span>
                    <span className="text-slate-800 font-bold block capitalize">{company.verificationStatus}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact & Location */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Contact Details</h5>
              <div className="space-y-2.5 pl-1">
                {[company.addressLine1, company.addressLine2, company.city, company.state, company.postalCode, company.country].filter(Boolean).join(", ") && (
                  <div className="flex items-start gap-2 text-xs text-slate-600 font-medium">
                    <MapPin className="size-4 text-slate-400 shrink-0 mt-0.5" />
                    <span>{[company.addressLine1, company.addressLine2, company.city, company.state, company.postalCode, company.country].filter(Boolean).join(", ")}</span>
                  </div>
                )}
                {company.website && (
                  <div className="flex items-center gap-2 text-xs text-primary font-medium hover:underline">
                    <ExternalLink className="size-4 text-slate-400 shrink-0" />
                    <a href={company.website.startsWith("http") ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer">
                      {company.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </>,
    document.body
  )
}

// Product Detail Sidebar Component (Sliding Drawer) 
export interface ProductDetailSidebarProps {
  product?: any
  productId?: string
  onClose: () => void
  onCompanyClick?: (company: any) => void
  showCompanySpec?: boolean
  level?: number
}

export function ProductDetailSidebar({
  product: propProduct,
  productId: propProductId,
  onClose,
  onCompanyClick,
  showCompanySpec = true,
  level = 0,
}: ProductDetailSidebarProps) {
  const fetchId = propProductId || (!propProduct ? propProduct?.id : undefined)
  const { data: fetchedProduct, isLoading, isError, error } = useProductDetails(fetchId)
  const product = fetchedProduct || propProduct

  if ((!fetchId && !propProduct) || typeof document === "undefined") return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200"
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose}
      />
      
      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <Package className="size-4 text-primary" /> Product Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Sidebar Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-left text-xs font-semibold text-slate-700 scrollbar-thin">
          {isLoading && !product && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
              <Loader2 className="size-6 animate-spin text-primary" />
              <p>Loading product details...</p>
            </div>
          )}

          {isError && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-destructive font-semibold flex items-center gap-2">
              <AlertCircle className="size-4.5 shrink-0" />
              <span>{(error as Error)?.message || "Failed to load product details."}</span>
            </div>
          )}

          {!isLoading && !isError && product ? (
            <>
              {/* Product Basic Info */}
              <div className="space-y-3.5">
                {product.images && product.images.length > 0 && product.images[0]?.url && (
                  <div className="w-full h-36 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm">
                    <img
                      src={product.images[0].url}
                      alt={product.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {product.productName && (
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Product Name</span>
                    <span className="text-sm font-extrabold text-slate-900">{product.productName}</span>
                  </div>
                )}
                {product.description && (
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Description</span>
                    <p className="text-slate-600 leading-relaxed font-normal">{product.description}</p>
                  </div>
                )}
              </div>

              {/* Category info */}
              {(product.productCategory || product.productSubCategory || product.productCode) && (
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  {product.productCode && (
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Product Code</span>
                      <span className="text-xs font-bold text-slate-800 font-mono">
                        {product.productCode}
                      </span>
                    </div>
                  )}
                  {product.productCategory && (
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Category</span>
                      <span className="text-xs font-bold text-slate-800">
                        {product.productCategory}
                      </span>
                    </div>
                  )}
                  {product.productSubCategory && (
                    <div className="col-span-2 mt-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Sub-Category</span>
                      <span className="text-xs font-bold text-slate-800">
                        {product.productSubCategory}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Tags</span>
                  <div className="flex flex-wrap gap-1.5">
                    {product.tags.map((t: string, i: number) => (
                      <span key={i} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-bold border border-slate-200/60 uppercase">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Parent Company Info */}
              {showCompanySpec && product.company && (
                <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
                  <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <Building2 className="size-3.5" /> Company Specifications
                  </h4>
                  <div  className="flex items-center gap-2 text-xs text-primary font-medium hover:underline">
                    <span className="flex items-center gap-1.5 text-sm font-extrabold text-slate-700 cursor-pointer hover:text-primary hover:underline transition-colors">
                      <ExternalLink className="size-4 text-slate-400 shrink-0" />
                    </span>
                    <span 
                      className={cn(
                        "text-slate-700 text-sm font-extrabold",
                        onCompanyClick && "hover:text-primary cursor-pointer hover:underline transition-colors"
                      )}
                      onClick={() => onCompanyClick?.(product.company)}
                    >
                      {product.company.name}
                    </span>
                  </div>
                </div>
              )}
            </>
          ) : !isLoading && !isError ? (
            <div className="text-center text-slate-400 py-10 font-semibold">
              No product data available.
            </div>
          ) : null}
        </div>

        {/* Sidebar Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </>,
    document.body
  )
}

// Variant Detail Sidebar Component (Sliding Drawer) 
export interface VariantDetailSidebarProps {
  variant?: any
  variantId?: string
  onClose: () => void
  onCompanyClick?: (company: any) => void
  showCompanySpec?: boolean
  level?: number
}

export function VariantDetailSidebar({
  variant: propVariant,
  variantId: propVariantId,
  onClose,
  onCompanyClick,
  showCompanySpec = true,
  level = 0,
}: VariantDetailSidebarProps) {
  const fetchId = propVariantId || (!propVariant ? propVariant?.id : undefined)
  const { data: fetchedVariant, isLoading, isError, error } = useProductVariantDetails(fetchId)
  const variant = fetchedVariant || propVariant

  if ((!fetchId && !propVariant) || typeof document === "undefined") return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200"
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose}
      />
      
      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <Package className="size-4 text-primary" /> Variant Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Sidebar Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-left text-xs font-semibold text-slate-700 scrollbar-thin">
          {isLoading && !variant && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
              <Loader2 className="size-6 animate-spin text-primary" />
              <p>Loading variant details...</p>
            </div>
          )}

          {isError && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-destructive font-semibold flex items-center gap-2">
              <AlertCircle className="size-4.5 shrink-0" />
              <span>{(error as Error)?.message || "Failed to load variant details."}</span>
            </div>
          )}

          {!isLoading && !isError && variant ? (
            <>
              {/* Variant Basic Info */}
              <div className="space-y-3.5">
                {variant.name && (
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Variant Name</span>
                    <span className="text-sm font-extrabold text-slate-900">{variant.name}</span>
                  </div>
                )}
                {variant.description && (
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Description</span>
                    <p className="text-slate-600 leading-relaxed font-normal">{variant.description}</p>
                  </div>
                )}
              </div>

              {/* Price & Stock */}
              {((variant.price !== null && variant.price !== undefined) || (variant.stockQuantity !== null && variant.stockQuantity !== undefined)) && (
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  {variant.price !== null && variant.price !== undefined && (
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Price</span>
                      <span className="text-sm font-bold text-primary font-mono">
                        {variant.currencyCode || "INR"} {variant.price}
                      </span>
                    </div>
                  )}
                  {variant.stockQuantity !== null && variant.stockQuantity !== undefined && (
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Stock Quantity</span>
                      <span className="text-sm font-bold text-slate-800">
                        {variant.stockQuantity}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Metadata */}
              {(variant.sku || variant.hsnCode) && (
                <div className="grid grid-cols-2 gap-4 pt-1">
                  {variant.sku && (
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">SKU</span>
                      <span className="text-slate-850 font-mono">{variant.sku}</span>
                    </div>
                  )}
                  {variant.hsnCode && (
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">HSN Code</span>
                      <span className="text-slate-850 font-mono">{variant.hsnCode}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {variant.tags && variant.tags.length > 0 && (
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Tags</span>
                  <div className="flex flex-wrap gap-1.5">
                    {variant.tags.map((t: string, i: number) => (
                      <span key={i} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-bold border border-slate-200/60 uppercase">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Parent Product Info */}
              {variant.product && (variant.product.productName || variant.product.productCode || variant.product.productCategory || variant.product.productSubCategory) && (
                <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
                  <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <Layers className="size-3.5" /> Product Specifications
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {variant.product.productName && (
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Product Name</span>
                        <span className="text-slate-800">{variant.product.productName}</span>
                      </div>
                    )}
                    {variant.product.productCode && (
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Product Code</span>
                        <span className="text-slate-800 font-mono">{variant.product.productCode}</span>
                      </div>
                    )}
                    {variant.product.productCategory && (
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Category</span>
                        <span className="text-slate-800">{variant.product.productCategory}</span>
                      </div>
                    )}
                    {variant.product.productSubCategory && (
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Sub-Category</span>
                        <span className="text-slate-800">{variant.product.productSubCategory}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Parent Company Info */}
              {showCompanySpec && (variant.company || variant.product?.company) && (() => {
                const comp = variant.company || variant.product?.company
                return (
                  <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
                    <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                      <Building2 className="size-3.5" /> Company Specifications
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-primary font-medium">
                      <span 
                        className={cn(
                          "flex items-center gap-1.5 text-sm font-extrabold text-slate-700",
                          onCompanyClick && "hover:text-primary cursor-pointer hover:underline transition-colors"
                        )}
                        onClick={() => onCompanyClick?.(comp)}
                      >
                        <ExternalLink className="size-4 text-slate-400 shrink-0" />
                        {comp.name || comp.companyName || "View Company"}
                      </span>
                    </div>
                  </div>
                )
              })()}
            </>
          ) : !isLoading && !isError ? (
            <div className="text-center text-slate-400 py-10 font-semibold">
              No variant data available.
            </div>
          ) : null}
        </div>

        {/* Sidebar Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </>,
    document.body
  )
}

// External Seller Detail Sidebar Component (Sliding Drawer) 
export interface ExternalSellerDetailSidebarProps {
  externalSeller: any | null
  onClose: () => void
  level?: number
}

export function ExternalSellerDetailSidebar({ externalSeller, onClose, level = 0 }: ExternalSellerDetailSidebarProps) {
  const [isDescExpanded, setIsDescExpanded] = React.useState(false)

  if (!externalSeller || typeof document === "undefined") return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200" 
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose} 
      />
      
      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <Building2 className="size-4 text-primary" /> External Supplier Profile
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Sidebar Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-left text-xs font-semibold text-slate-700 scrollbar-thin">
          <div className="space-y-5">
            {/* Basic Info */}
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-0.5">Company Name</span>
              <h4 className="text-sm font-extrabold text-slate-900 leading-tight">{externalSeller.companyName || externalSeller.sellerName}</h4>
              
              {externalSeller.description && (
                <div className="mt-1">
                  <p className="text-slate-600 leading-relaxed font-normal whitespace-pre-line inline">
                    {externalSeller.description.length > 150 && !isDescExpanded
                      ? `${externalSeller.description.slice(0, 150)}...`
                      : externalSeller.description}
                  </p>
                  {externalSeller.description.length > 150 && (
                    <button
                      onClick={() => setIsDescExpanded(!isDescExpanded)}
                      className="ml-1 text-primary hover:text-primary/80 hover:underline font-bold text-[11px] cursor-pointer"
                    >
                      {isDescExpanded ? "Read Less" : "Read More"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Profile Grid */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Company Overview</h5>
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                {externalSeller.businessType && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Business Type</span>
                    <span className="text-slate-800 font-bold block">{externalSeller.businessType}</span>
                  </div>
                )}
                {externalSeller.establishment && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Established</span>
                    <span className="text-slate-800 font-bold block">{externalSeller.establishment}</span>
                  </div>
                )}
                {externalSeller.employeeCount && (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Employees</span>
                    <span className="text-slate-800 font-bold block">{externalSeller.employeeCount}</span>
                  </div>
                )}
                {externalSeller.annualTurnover && (
                  <div className="space-y-0.5 col-span-2 mt-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Annual Turnover</span>
                    <span className="text-slate-800 font-bold block">{externalSeller.annualTurnover}</span>
                  </div>
                )}
                {externalSeller.proprietor && (
                  <div className="space-y-0.5 col-span-2 mt-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Proprietor</span>
                    <span className="text-slate-800 font-bold block">{externalSeller.proprietor}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact & Location */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Location & Contact</h5>
              <div className="space-y-2.5 pl-1">
                {[externalSeller.streetAddress, externalSeller.locality, externalSeller.region, externalSeller.postalCode, externalSeller.country].filter(Boolean).join(", ") && (
                  <div className="flex items-start gap-2 text-xs text-slate-600 font-medium">
                    <MapPin className="size-4 text-slate-400 shrink-0 mt-0.5" />
                    <span>{[externalSeller.streetAddress, externalSeller.locality, externalSeller.region, externalSeller.postalCode, externalSeller.country?.toUpperCase()].filter(Boolean).join(", ")}</span>
                  </div>
                )}
                {externalSeller.companyUrl && (
                  <div className="flex items-center gap-2 text-xs text-primary font-medium hover:underline">
                    <ExternalLink className="size-4 text-slate-400 shrink-0" />
                    <a href={externalSeller.companyUrl.startsWith("http") ? externalSeller.companyUrl : `https://${externalSeller.companyUrl}`} target="_blank" rel="noopener noreferrer">
                      {externalSeller.companyUrl}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </>,
    document.body
  )
}

// RFQ Detail Sidebar Component (Sliding Drawer) 
export interface RfqDetailSidebarProps {
  rfq: any | null
  onClose: () => void
  isSeller?: boolean
  level?: number
}

export function RfqDetailSidebar({ rfq, onClose, isSeller = false, level = 0 }: RfqDetailSidebarProps) {
  const [selectedProduct, setSelectedProduct] = React.useState<any | null>(null)
  const [selectedProductId, setSelectedProductId] = React.useState<string | null>(null)
  const [selectedVariant, setSelectedVariant] = React.useState<any | null>(null)
  const [selectedVariantId, setSelectedVariantId] = React.useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = React.useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = React.useState<any | null>(null)

  if (!rfq || typeof document === "undefined") return null

  const rfqStatus = rfq.status || 'UNKNOWN'

  const statusStyle =
    rfqStatus === 'ACTIVE' || rfqStatus === 'ACCEPTED' || rfqStatus === 'OPEN'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : rfqStatus === 'PENDING' || rfqStatus === 'QUOTED'
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : rfqStatus === 'CLOSED' || rfqStatus === 'REJECTED'
      ? 'bg-red-50 text-red-700 border-red-100'
      : 'bg-slate-50 text-slate-700 border-slate-200'

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200"
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose}
      />

      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >

        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <FileText className="size-4 text-primary" /> RFQ Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Sidebar Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-left text-xs font-semibold text-slate-700 scrollbar-thin">
          <div className="space-y-5">

            {/* Basic Info: Message + Status */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {rfq.queryMessage ? (
                    <p className="text-slate-800 text-sm font-extrabold leading-relaxed whitespace-pre-line">
                      {rfq.queryMessage}
                    </p>
                  ) : (
                    <p className="text-slate-400 text-sm font-bold italic">No reference message</p>
                  )}
                </div>
                <span className={`shrink-0 inline-flex items-center rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${statusStyle}`}>
                  {rfqStatus}
                </span>
              </div>
            </div>

            {/* RFQ Overview Grid */}
            {(rfq.quantity || rfq.budget || rfq.paymentTerms || rfq.leadTime) && (
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Specification</h5>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  {rfq.quantity && (
                    <div className="space-y-0.5">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Quantity</span>
                      <span className="text-slate-800 font-bold block">{rfq.quantity + " " + rfq.measureUnit}</span>
                    </div>
                  )}
                  {rfq.budget && (
                    <div className="space-y-0.5">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Budget</span>
                      <span className="text-slate-800 font-bold block">{rfq.currencyCode + " " + rfq.budget}</span>
                    </div>
                  )}
                  {rfq.paymentTerms && (
                    <div className="space-y-0.5">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Payment Terms</span>
                      <span className="text-slate-800 font-bold block">{rfq.paymentTerms}</span>
                    </div>
                  )}
                  {rfq.leadTime && (
                    <div className="space-y-0.5">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Lead Time</span>
                      <span className="text-slate-800 font-bold block">{formatDate(rfq.leadTime)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delivery Info */}
            {rfq.address && (
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Delivery & Location</h5>
                <div className="space-y-2.5 pl-1">
                  <div className="flex items-start gap-2 text-xs text-slate-800 font-medium">
                    <MapPin className="size-4 text-slate-400 shrink-0" />
                    <span className="text-slate-800 font-bold">{rfq.address}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Target Sourcing Company */}
            {rfq.company && !isSeller && (
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Target Sourcing Company
                </h5>
                <div className="space-y-2">
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <div
                      onClick={() => setSelectedCompany(rfq.company)}
                      className="flex items-center justify-between group cursor-pointer hover:bg-white p-1.5 -mx-1.5 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white shadow-sm text-slate-500 group-hover:border-slate-300 group-hover:text-black transition-colors">
                          <Building2 className="size-3.5" />
                        </div>
                        <div>
                          <span className="text-xs font-bold text-slate-800 group-hover:text-black transition-colors font-sans">
                            {rfq.company.name || rfq.company.companyName || 'Unknown Company'}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="size-3.5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Requested Items */}
            {(() => {
              const validTargets = (rfq.targets || []).filter(
                (target: any) => target.product || target.productVariant || target.productId || target.productVariantId
              )
              if (validTargets.length === 0) return null

              return (
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                    Requested Items ({validTargets.length})
                  </h5>
                  <div className="space-y-2">
                    {validTargets.map((target: any, idx: number) => (
                      <div key={target.id || idx} className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2">

                        {/* Target Product */}
                        {(target.product || target.productId) && (
                          <div
                            onClick={() => {
                              if (target.product) {
                                setSelectedProduct(target.product)
                              } else if (target.productId) {
                                setSelectedProductId(target.productId)
                              }
                            }}
                            className="flex items-center justify-between group cursor-pointer hover:bg-white p-1.5 -mx-1.5 rounded-lg transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-primary/15 bg-white shadow-sm text-primary group-hover:border-primary/30 transition-colors">
                                <Package className="size-3.5" />
                              </div>
                              <div>
                                <span className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors block leading-tight">
                                  {target.product?.productName || 'View Product Details'}
                                </span>
                                {target.quantity && <span className="text-[10px] text-slate-400 font-medium">Qty: {target.quantity}</span>}
                              </div>
                            </div>
                            <ChevronRight className="size-3.5 text-slate-300 group-hover:text-primary transition-colors" />
                          </div>
                        )}

                        {/* Target Variant */}
                        {(target.productVariant || target.productVariantId) && (
                          <div
                            onClick={() => {
                              if (target.productVariant) {
                                setSelectedVariant(target.productVariant)
                              } else if (target.productVariantId) {
                                setSelectedVariantId(target.productVariantId)
                              }
                            }}
                            className="flex items-center justify-between group cursor-pointer hover:bg-white p-1.5 -mx-1.5 rounded-lg transition-colors border-t border-slate-200/60 pt-2"
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-primary/15 bg-white shadow-sm text-primary group-hover:border-primary/30 transition-colors">
                                <Layers className="size-3.5" />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Variant</span>
                                <span className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">
                                  {target.productVariant?.name || target.productVariant?.variantName || 'View Variant Details'}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="size-3.5 text-slate-300 group-hover:text-primary transition-colors" />
                          </div>
                        )}

                        {/* Target Company */}
                        {target.company && !isSeller && (
                          <div
                            onClick={() => setSelectedCompany(target.company)}
                            className="flex items-center justify-between group cursor-pointer hover:bg-white p-1.5 -mx-1.5 rounded-lg transition-colors border-t border-slate-200/60 pt-2"
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-primary/15 bg-white shadow-sm text-primary group-hover:border-primary/30 transition-colors">
                                <Building2 className="size-3.5" />
                              </div>
                              <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Company</span>
                                <span className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">
                                  {target.company.name || target.company.companyName || 'Unknown Company'}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="size-3.5 text-slate-300 group-hover:text-primary transition-colors" />
                          </div>
                        )}

                        {/* Target External Sellers */}
                        {target.externalCompanyNames && target.externalCompanyNames.length > 0 && (
                          <div className="border-t border-slate-200/60 pt-2 space-y-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block mb-1">External Sellers</span>
                            {target.externalCompanyNames.map((extName: string, eIdx: number) => (
                              <div
                                key={eIdx}
                                onClick={() => setSelectedExternalSeller({ companyName: extName, id: target.externalSellerId })}
                                className="flex items-center justify-between group cursor-pointer hover:bg-white p-1.5 -mx-1.5 rounded-lg transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-primary/15 bg-white shadow-sm text-primary group-hover:border-primary/30 transition-colors">
                                    <Building2 className="size-3.5" />
                                  </div>
                                  <span className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">{extName}</span>
                                </div>
                                <ChevronRight className="size-3 text-slate-300 group-hover:text-primary transition-colors" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>

      {/* Nested Sidebars */}
      {(selectedProduct || selectedProductId) && (
        <ProductDetailSidebar
          product={selectedProduct}
          productId={selectedProductId || undefined}
          onClose={() => { setSelectedProduct(null); setSelectedProductId(null); }}
          onCompanyClick={(c) => setSelectedCompany(c)}
          showCompanySpec={!isSeller}
          level={level + 1}
        />
      )}
      {(selectedVariant || selectedVariantId) && (
        <VariantDetailSidebar
          variant={selectedVariant}
          variantId={selectedVariantId || undefined}
          onClose={() => { setSelectedVariant(null); setSelectedVariantId(null); }}
          onCompanyClick={(c) => setSelectedCompany(c)}
          showCompanySpec={!isSeller}
          level={level + 1}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          level={(selectedProduct || selectedVariant || selectedProductId || selectedVariantId) ? level + 2 : level + 1}
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar
          externalSeller={selectedExternalSeller}
          onClose={() => setSelectedExternalSeller(null)}
          level={level + 1}
        />
      )}
    </>,
    document.body
  )
}   

// ── Quotation Detail Sidebar Component (Sliding Drawer) ────────────────────────
export interface QuotationDetailSidebarProps {
  quotation: any | null
  onClose: () => void
  showLinkedRfq?: boolean
  level?: number
}

export function QuotationDetailSidebar({ quotation, onClose, showLinkedRfq = true, level = 0 }: QuotationDetailSidebarProps) {
  const [selectedProductId, setSelectedProductId] = React.useState<string | null>(null)
  const [selectedVariantId, setSelectedVariantId] = React.useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = React.useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = React.useState<any | null>(null)
  const [selectedRfq, setSelectedRfq] = React.useState<any | null>(null)

  if (!quotation || typeof document === "undefined") return null

  const q = quotation
  const charges: any[] = q.charges || q.adminToBuyerQuotationCharges || q.sellerGeneratedQuotationCharges || []

  const qStatus = q.status || 'UNKNOWN'
  const statusStyle =
    qStatus === 'APPROVED' || qStatus === 'ACCEPTED'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : qStatus === 'PENDING' || qStatus === 'QUOTED'
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : qStatus === 'REJECTED' || qStatus === 'CLOSED' || qStatus === 'CANCELLED'
      ? 'bg-red-50 text-red-700 border-red-100'
      : 'bg-slate-50 text-slate-700 border-slate-200'

  // Compute grand total from charges
  const grandTotal = charges.reduce((sum: number, c: any) => {
    const qty = (c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1
    const base = (c.unitPrice ?? 0) * qty
    const discount = c.discount ?? 0
    const tax = c.tax ?? 0
    const discountAmount = (base * discount) / 100
    const afterDiscount = base - discountAmount
    const taxAmount = (afterDiscount * tax) / 100
    return sum + afterDiscount + taxAmount
  }, 0)

  // Sourcing info from nested quotation chain
  const sellerToAdminQuotation = q.sellerToAdminQuotation
  const adminToSellerRfq = q.rfq || q.adminToSellerRfq || sellerToAdminQuotation?.adminToSellerRfq
  const rfqObj = q.rfq || q.buyerToAdminRfq || adminToSellerRfq || adminToSellerRfq?.rfq || null
  const sourcingCompany = sellerToAdminQuotation?.company

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200"
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose}
      />

      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[380px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >

        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <FileText className="size-4 text-primary" /> Quotation Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Sidebar Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6 text-left text-xs font-semibold text-slate-700 scrollbar-thin">

          {/* Basic Info: Message + Status */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {q.message ? (
                  <p className="text-slate-800 text-sm font-bold leading-relaxed whitespace-pre-line">
                    {q.message}
                  </p>
                ) : (
                  <p className="text-slate-400 text-sm font-bold italic">No message provided</p>
                )}
              </div>
              <span className={`shrink-0 inline-flex items-center rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${statusStyle}`}>
                {qStatus}
              </span>
            </div>
          </div>

          {/* Overview Grid */}
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Overview</h5>
            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
              {q.currencyCode && (
                <div className="space-y-0.5">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Currency</span>
                  <span className="text-slate-800 font-bold block font-mono">{q.currencyCode}</span>
                </div>
              )}
              {q.version !== undefined && (
                <div className="space-y-0.5">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Version</span>
                  <span className="text-slate-800 font-bold block">v{q.version}</span>
                </div>
              )}
              {q.createdAt && (
                <div className="space-y-0.5">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Created</span>
                  <span className="text-slate-800 font-bold block">{new Date(q.createdAt).toLocaleDateString()}</span>
                </div>
              )}
              {q.updatedAt && (
                <div className="space-y-0.5">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Last Updated</span>
                  <span className="text-slate-800 font-bold block">{new Date(q.updatedAt).toLocaleDateString()}</span>
                </div>
              )}
              {(q.expiresAt || q.validUntil || q.sellerToAdminQuotation?.expiresAt) && (() => {
                const expiry = q.expiresAt || q.validUntil || q.sellerToAdminQuotation?.expiresAt
                const isPast = new Date(expiry).getTime() < Date.now()
                return (
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide block">Valid Until</span>
                    <span className="text-slate-800 font-bold flex items-center gap-1.5 flex-wrap">
                      {new Date(expiry).toLocaleDateString()}
                      {isPast && (
                        <span className="text-[8px] font-bold text-red-600 bg-red-50 border border-red-200 px-1 py-0.2 rounded uppercase">
                          Expired
                        </span>
                      )}
                    </span>
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Charges Breakdown */}
          {charges.length > 0 && (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Charges Breakdown</h5>
              <div className="space-y-2">
                {charges.map((c: any, idx: number) => {
                  const qty = (c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1
                  const base = (c.unitPrice ?? 0) * qty
                  const discount = c.discount ?? 0
                  const tax = c.tax ?? 0
                  const discountAmount = (base * discount) / 100
                  const afterDiscount = base - discountAmount
                  const taxAmount = (afterDiscount * tax) / 100
                  const rowTotal = afterDiscount + taxAmount
                  return (
                    <div key={c.id || idx} className="bg-white border border-slate-100 rounded-lg p-3 space-y-2 shadow-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-800 text-xs">{c.description || `Charge ${idx + 1}`}</span>
                        {c.taxType && (
                           <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase tracking-wide border border-slate-200">{c.taxType}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-slate-500">
                        {c.unitPrice !== undefined && (
                          <div className="flex justify-between">
                            <span>Unit Price</span>
                            <span className="font-bold text-slate-700 font-mono">{q.currencyCode} {Number(c.unitPrice).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span>Qty</span>
                          <span className="font-bold text-slate-700">
                            {c.quantity !== null && c.quantity !== undefined && c.quantity !== ""
                              ? `${c.quantity}${(c.measureUnit || c.unit) ? ` ${c.measureUnit || c.unit}` : ''}`
                              : '-'}
                          </span>
                        </div>
                        {discount > 0 && (
                          <div className="flex justify-between">
                            <span>Discount ({discount}%)</span>
                            <span className="font-bold text-emerald-600 font-mono">-{discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        {tax > 0 && (
                          <div className="flex justify-between">
                            <span>Tax ({tax}%)</span>
                            <span className="font-bold text-slate-700 font-mono">+{taxAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-100 pt-1.5 mt-1">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Row Total</span>
                        <span className="text-primary font-extrabold font-mono text-xs">{q.currencyCode} {rowTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )
                })}

                {/* Grand Total */}
                <div className="flex items-center justify-between bg-slate-900 rounded-xl px-4 py-3 mt-2">
                  <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Grand Total</span>
                  <span className="text-white font-extrabold font-mono text-sm">{q.currencyCode} {grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Linked RFQ */}
          {showLinkedRfq && (rfqObj || q.rfqId) && (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <FileText className="size-3.5" /> Linked RFQ
              </h5>
              <div
                onClick={() => rfqObj ? setSelectedRfq(rfqObj) : undefined}
                className={`flex items-center justify-between gap-3 rounded-xl border border-primary/15 p-3 shadow-sm transition-all duration-200 ${rfqObj ? 'bg-slate-50/40 hover:bg-slate-50 hover:border-primary/30 cursor-pointer' : 'bg-slate-50/20 opacity-70 cursor-default'}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-extrabold text-primary text-xs font-sans truncate">
                    {rfqObj?.queryMessage || (rfqObj?.id || q.rfqId ? `#${(rfqObj?.id || q.rfqId).slice(0, 8).toUpperCase()}` : 'View Linked RFQ')}
                  </span>
                </div>
                {rfqObj && <ChevronRight className="size-3.5 text-slate-300" />}
              </div>
            </div>
          )}

          {/* Sourcing Info */}
          {(sourcingCompany) && (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <h5 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Sourcing Info</h5>

              {sourcingCompany && (
                <div
                  onClick={() => setSelectedCompany(sourcingCompany)}
                  className="flex items-center justify-between group cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="size-3.5 text-primary" />
                    <div>
                      <span className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">{sourcingCompany.name || sourcingCompany.companyName}</span>
                      {sourcingCompany.country && <span className="text-[9px] text-slate-400 block">{sourcingCompany.country}</span>}
                    </div>
                  </div>
                  <ChevronRight className="size-3.5 text-slate-300 group-hover:text-primary" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>

      {/* Nested Sidebars */}
      {selectedRfq && (
        <RfqDetailSidebar
          rfq={{
            ...selectedRfq,
            company: selectedRfq.company || q?.company
          }}
          onClose={() => setSelectedRfq(null)}
          level={level + 1}
        />
      )}
      {selectedProductId && (
        <ProductDetailSidebar
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          level={level + 1}
        />
      )}
      {selectedVariantId && (
        <VariantDetailSidebar
          variantId={selectedVariantId}
          onClose={() => setSelectedVariantId(null)}
          level={level + 1}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          level={level + 1}
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar
          externalSeller={selectedExternalSeller}
          onClose={() => setSelectedExternalSeller(null)}
          level={level + 1}
        />
      )}
    </>,
    document.body
  )
}

// ── Quotation History Timeline Sidebar ──────────────────────────────────────────
export interface QuotationHistoryTimelineSidebarProps {
  /** All quotations for a single company, sorted by version internally */
  quotations: any[]
  onClose: () => void
  onSelectLatest?: (quotation?: any) => void
  level?: number
}

export function QuotationHistoryTimelineSidebar({ quotations, onClose, onSelectLatest, level = 0 }: QuotationHistoryTimelineSidebarProps) {
  const [selectedHistoryQuotation, setSelectedHistoryQuotation] = React.useState<any | null>(null)

  const handleLatestClick = (q: any) => {
    onClose()
    onSelectLatest?.(q)
  }

  if (!quotations || quotations.length === 0 || typeof document === "undefined") return null

  // Sort by version descending: latest (Vn) first
  const sorted = [...quotations].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))

  const getStatusStyle = (status: string) => {
    const s = (status || '').toUpperCase()
    if (s === 'ACCEPTED' || s === 'APPROVED') return 'bg-emerald-50 text-emerald-700 border-emerald-100'
    if (s === 'PENDING' || s === 'QUOTED') return 'bg-amber-50 text-amber-700 border-amber-100'
    if (s === 'SUPERSEDED') return 'bg-slate-100 text-slate-500 border-slate-200'
    if (s === 'REJECTED' || s === 'CLOSED' || s === 'CANCELLED') return 'bg-red-50 text-red-700 border-red-100'
    return 'bg-slate-50 text-slate-700 border-slate-200'
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 animate-in fade-in duration-200"
        style={{ zIndex: 60 + level * 10 }}
        onClick={onClose}
      />

      {/* Sliding Sidebar Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
        style={{ zIndex: 99 + level * 10 }}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 font-sans">
            <Clock className="size-4 text-primary" /> Quote History
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="size-4.5" />
          </button>
        </div>

        {/* Supplier strip */}
        {sorted[0]?.company && (
          <div className="px-5 py-3 bg-slate-50/80 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-sm font-black shrink-0 border border-primary/20">
                {sorted[0].company.name?.[0]?.toUpperCase() ?? 'S'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-800 truncate">{sorted[0].company.name}</p>
                {sorted[0].company.country && (
                  <p className="text-[10px] text-slate-400 font-semibold">{sorted[0].company.country}</p>
                )}
              </div>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide shrink-0">
                {sorted.length} version{sorted.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Timeline Body */}
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-[14px] top-4 bottom-4 w-[2px] bg-slate-200 rounded-full" />

            <div className="space-y-1">
              {sorted.map((q, idx) => {
                const isLatestQ = idx === 0
                const version = q.version ?? (sorted.length - idx)

                const computedTotal = (q.charges || []).reduce((sum: number, c: any) => {
                  const base = (c.unitPrice ?? 0) * (c.quantity ?? 1)
                  const after = base - (base * (c.discount ?? 0) / 100)
                  return sum + after + (after * (c.tax ?? 0) / 100)
                }, 0)

                return (
                  <div key={q.id || idx} className="relative flex items-start gap-3.5 pb-5 last:pb-0">
                    {/* Timeline Node */}
                    <div className={`relative z-10 shrink-0 w-[28px] h-[28px] rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      isLatestQ
                        ? 'bg-primary border-primary text-primary-foreground shadow-sm'
                        : 'bg-white border-slate-300 text-slate-500 hover:border-primary/60 transition-colors'
                    }`}>
                      <span className="text-[8px] font-extrabold leading-none">V{version}</span>
                    </div>

                    {/* Card */}
                    <div
                      onClick={isLatestQ ? () => handleLatestClick(q) : () => setSelectedHistoryQuotation(q)}
                      className={`flex-1 min-w-0 rounded-xl border p-3 transition-all duration-200 cursor-pointer ${
                        isLatestQ
                          ? 'bg-primary/[0.03] border-primary/20 hover:bg-primary/[0.06] hover:border-primary/35'
                          : 'bg-white border-slate-200 hover:border-primary/30 hover:bg-slate-50/80'
                      }`}
                    >
                      {/* Top row: version label + status badge */}
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-xs font-extrabold text-slate-800 truncate">
                            Version {version}
                          </span>
                          {isLatestQ && (
                            <span className="shrink-0 text-[8px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                              Latest
                            </span>
                          )}
                        </div>
                        <span className={`shrink-0 inline-flex items-center rounded px-2 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${getStatusStyle(q.status)}`}>
                          {q.status}
                        </span>
                      </div>

                      {/* Date */}
                      <p className="text-[10px] text-slate-400 font-semibold mb-1">
                        {new Date(q.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>

                      {/* Message preview */}
                      {q.message && (
                        <p className="text-[11px] text-slate-600 font-semibold italic line-clamp-1 mb-1.5">
                          "{q.message}"
                        </p>
                      )}

                      {/* Computed total */}
                      {q.charges && q.charges.length > 0 && (
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-1.5">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Total</span>
                          <span className="text-[11px] font-extrabold text-primary font-mono">
                            {q.currencyCode} {computedTotal.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* CTA hint */}
                      {!isLatestQ && (
                        <p className="text-[9px] font-bold mt-2 flex items-center gap-0.5 text-slate-400">
                          View details <ChevronRight className="size-3" />
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>

      {/* Nested: older version detail sidebar — renders later in DOM so sits on top due to stacking order */}
      {selectedHistoryQuotation && (
        <QuotationDetailSidebar
          quotation={selectedHistoryQuotation}
          onClose={() => setSelectedHistoryQuotation(null)}
          showLinkedRfq={false}
          level={level + 1}
        />
      )}
    </>,
    document.body
  )
}